import { useEffect, useRef, useState, useCallback } from "react";
import { X, MessageCircle, ArrowRight, Volume2, VolumeX } from "lucide-react";
import { getWhatsAppLink } from "@/lib/contact";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/lib/i18n";

const CLIPS = [
  {
    src: "/promo/china-clip-01-factory.mp4",
    duration: 7,
    headline: "¿Buscas proveedores en China\nsin saber si son reales?",
    sub: null,
  },
  {
    src: "/promo/china-clip-02-inspection.mp4",
    duration: 8,
    headline: "Verificamos fábricas\ne inspeccionamos la mercancía.",
    sub: "Documentamos cada hallazgo antes de pagar o embarcar.",
  },
  {
    src: "/promo/china-clip-03-merchandise.mp4",
    duration: 8,
    headline: "Evidencia fotográfica.\nReportes claros. Condiciones verificadas.",
    sub: "Desde el origen, sin sorpresas al llegar.",
  },
  {
    src: "/promo/china-clip-04-departure.mp4",
    duration: 7,
    headline: "Tu compra en China,\nrespaldada por expertos.",
    sub: null,
  },
];

const EN_CLIPS = [
  { headline: "Looking for suppliers in China\nbut not sure they are real?", sub: null },
  { headline: "We verify factories\nand inspect your cargo.", sub: "We document every finding before you pay or ship." },
  { headline: "Photo evidence.\nClear reports. Verified terms.", sub: "From origin, without surprises on arrival." },
  { headline: "Your China purchase,\nbacked by experts.", sub: null },
];

const TOTAL_DURATION = CLIPS.reduce((s, c) => s + c.duration, 0); // 30 s

export default function PromoModal2() {
  const [open, setOpen] = useState(false);
  const [clipIdx, setClipIdx] = useState(0);
  const [ended, setEnded] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [muted, setMuted] = useState(false);
  const [textVisible, setTextVisible] = useState(true);
  const [audioReady, setAudioReady] = useState(false);
  const [audioNeedsGesture, setAudioNeedsGesture] = useState(false);
  const location = useLocation();
  const { isEnglish, t } = useLanguage();

  const narrationRef = useRef(null);
  const musicRef = useRef(null);
  const videoRef = useRef(null);
  const timerRef = useRef(null);
  const elapsedRef = useRef(0);

  const startAudio = useCallback(() => {
    const narration = narrationRef.current;
    const music = musicRef.current;
    const players = [narration, music].filter(Boolean);
    if (!players.length) {
      setAudioReady(true);
      setAudioNeedsGesture(false);
      return Promise.resolve();
    }
    if (narration) { narration.muted = muted; narration.volume = 1; }
    if (music)     { music.muted = muted;     music.volume = 0.14; }

    return Promise.all(players.map((p) => p.play()))
      .then(() => {
        setAudioReady(true);
        setAudioNeedsGesture(false);
      })
      .catch(() => {
        players.forEach((p) => p.pause());
        setAudioReady(false);
        setAudioNeedsGesture(true);
      });
  }, [muted]);

  // Show every time the user enters Inicio
  useEffect(() => {
    let timer;
    if (location.pathname === "/") {
      timer = setTimeout(() => {
        elapsedRef.current = 0;
        setElapsed(0);
        setClipIdx(0);
        setEnded(false);
        setTextVisible(true);
        setAudioReady(false);
        setAudioNeedsGesture(false);
        setOpen(true);
      }, 900);
    } else {
      clearInterval(timerRef.current);
      setOpen(false);
      setEnded(false);
      elapsedRef.current = 0;
      setElapsed(0);
      if (narrationRef.current) { narrationRef.current.pause(); narrationRef.current.currentTime = 0; }
      if (musicRef.current) { musicRef.current.pause(); musicRef.current.currentTime = 0; }
    }
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Try autoplay; retry from first user gesture if blocked
  useEffect(() => {
    if (!open) return;
    const retryFromGesture = () => void startAudio();
    void startAudio();
    window.addEventListener("pointerdown", retryFromGesture, { once: true, passive: true });
    window.addEventListener("keydown", retryFromGesture, { once: true });
    return () => {
      window.removeEventListener("pointerdown", retryFromGesture);
      window.removeEventListener("keydown", retryFromGesture);
    };
  }, [open, startAudio]);

  // Progress ticker
  useEffect(() => {
    if (!open || ended) return;
    timerRef.current = setInterval(() => {
      elapsedRef.current += 0.1;
      setElapsed(elapsedRef.current);
    }, 100);
    return () => clearInterval(timerRef.current);
  }, [open, ended]);

  // Clip transition logic
  useEffect(() => {
    if (!open) return;
    let offset = 0;
    for (let i = 0; i < CLIPS.length; i++) {
      if (elapsed >= offset && elapsed < offset + CLIPS[i].duration) {
        if (clipIdx !== i) {
          setTextVisible(false);
          setTimeout(() => {
            setClipIdx(i);
            setTextVisible(true);
          }, 200);
        }
        break;
      }
      offset += CLIPS[i].duration;
    }
    if (elapsed >= TOTAL_DURATION) {
      clearInterval(timerRef.current);
      setEnded(true);
    }
  }, [elapsed, open, clipIdx]);

  // Sync video element when clip changes
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !open) return;
    v.load();
    v.play().catch(() => {});
  }, [clipIdx, open]);

  // Mute/unmute
  useEffect(() => {
    if (narrationRef.current) narrationRef.current.muted = muted;
    if (musicRef.current) musicRef.current.muted = muted;
  }, [muted]);

  const handleClose = useCallback(() => {
    setOpen(false);
    if (narrationRef.current) { narrationRef.current.pause(); narrationRef.current.currentTime = 0; }
    if (musicRef.current) { musicRef.current.pause(); musicRef.current.currentTime = 0; }
    clearInterval(timerRef.current);
  }, []);

  const handleWhatsApp = () => {
    handleClose();
    window.open(
      getWhatsAppLink(
        "Hola NaviCargo, me interesa el servicio de búsqueda de productos en China y verificación de empresas."
      ),
      "_blank",
      "noopener,noreferrer"
    );
  };

  if (!open) return null;

  const progress = Math.min((elapsed / TOTAL_DURATION) * 100, 100);
   const clip = isEnglish ? { ...CLIPS[clipIdx], ...EN_CLIPS[clipIdx] } : CLIPS[clipIdx];

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
       aria-label={isEnglish ? "NaviCargo — China and company verification" : "NaviCargo — China y verificación de empresas"}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80" onClick={handleClose} />

      {/* Audio — narración China + música de fondo */}
      <audio ref={narrationRef} src={isEnglish ? "/promo/china-narration-en.mp3" : "/promo/china-narration.mp3"} preload="auto" />
      <audio ref={musicRef} src="/promo/navicargo-bg-music.mp3" preload="auto" loop />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-3xl rounded-2xl overflow-hidden border border-zinc-800/80 bg-black">

        {/* Progress bar */}
        <div className="absolute top-0 left-0 right-0 z-30 h-[3px] bg-zinc-800">
          <div
            className="h-full bg-[#22C55E] transition-none"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Top controls */}
        <div className="absolute top-4 left-4 right-4 z-30 flex items-center justify-end">
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (!audioReady) {
                  void startAudio();
                  return;
                }
                setMuted((v) => !v);
              }}
              className={`w-8 h-8 rounded-full bg-black/50 flex items-center justify-center transition-colors ${
                audioNeedsGesture && !muted ? "text-[#22C55E]" : "text-white/70 hover:text-white"
              }`}
               aria-label={!audioReady ? t("promo.enableSound", "Activar sonido") : muted ? t("promo.unmute", "Activar sonido") : t("promo.mute", "Silenciar")}
              aria-pressed={muted}
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-full bg-black/50 flex items-center justify-center text-white/70 hover:text-white transition-colors"
              aria-label="Cerrar video"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Video layer */}
        {!ended && (
          <div className="relative aspect-video bg-zinc-950">
            <video
              ref={videoRef}
              key={clip.src}
              className="w-full h-full object-cover"
              autoPlay
              muted
              playsInline
              loop
            >
              <source src={clip.src} type="video/mp4" />
            </video>

            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent" />

            {/* Service badge */}
            <div className="absolute top-10 left-8">
              <span className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700/60 bg-black/60 px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-zinc-400">
                 {isEnglish ? "China · Company verification" : "China · Verificación de empresas"}
              </span>
            </div>

            {/* Text overlay */}
            <div
              className="absolute bottom-10 left-8 right-8"
              style={{
                opacity: textVisible ? 1 : 0,
                transform: textVisible ? "translateY(0)" : "translateY(10px)",
                transition: "opacity 0.4s ease, transform 0.4s ease",
              }}
            >
              <p className="text-white text-2xl md:text-3xl font-bold leading-tight tracking-tight whitespace-pre-line drop-shadow-2xl mb-2">
                {clip.headline}
              </p>
              {clip.sub && (
                <p className="text-zinc-300 text-sm md:text-base font-medium drop-shadow-md">
                  {clip.sub}
                </p>
              )}
            </div>

            {/* Clip counter dots */}
            <div className="absolute bottom-4 right-6 flex items-center gap-1.5">
              {CLIPS.map((_, i) => (
                <span
                  key={i}
                  className="rounded-full transition-all duration-300"
                  style={{
                    width: i === clipIdx ? "20px" : "6px",
                    height: "6px",
                    background: i === clipIdx ? "#22C55E" : "rgba(255,255,255,0.3)",
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* End screen */}
        {ended && (
          <div className="relative aspect-video bg-zinc-950 flex flex-col items-center justify-center text-center px-8">
            <div className="absolute inset-0 bg-gradient-to-br from-[#22C55E]/8 via-zinc-950 to-zinc-950" />
            <div className="relative z-10 flex flex-col items-center gap-6">
              <img
                src="/navicargo-logo-white-transparent.png"
                alt="NaviCargo Logistics"
                className="h-10 w-auto"
              />
              <div>
                <p className="text-white text-2xl md:text-3xl font-bold tracking-tight mb-2">
                   {isEnglish ? "Buy from China with certainty." : "Compra en China con certeza."}
                </p>
                <p className="text-zinc-400 text-sm">
                   {isEnglish ? "We verify the supplier and inspect before you ship." : "Verificamos el proveedor e inspeccionamos antes de que embarques."}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <button
                  onClick={handleWhatsApp}
                  className="inline-flex items-center gap-2 bg-[#22C55E] text-black font-bold px-6 py-3 rounded-full hover:bg-[#22C55E]/90 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                   {t("common.talkAdvisor", "Hablar con un asesor")}
                </button>
                <Link
                  to="/services/verificacion-empresas"
                  onClick={handleClose}
                  className="inline-flex items-center gap-2 text-zinc-300 hover:text-white text-sm font-medium transition-colors"
                >
                   {isEnglish ? "View company verification" : "Ver verificación de empresas"}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Bottom strip */}
        <div className="bg-zinc-950 border-t border-zinc-800/60 px-6 py-3 flex items-center justify-between gap-4">
          <p className="text-zinc-500 text-xs">
            {ended
               ? `NaviCargo Logistics — ${isEnglish ? "Global logistics · MX" : "Logística global · MX"}`
               : `${Math.ceil(TOTAL_DURATION - elapsed)}${isEnglish ? "s remaining" : "s restantes"}`}
          </p>
          <button
            onClick={handleClose}
            className="text-zinc-500 hover:text-zinc-300 text-xs transition-colors"
          >
             {ended ? (isEnglish ? "Close" : "Cerrar") : (isEnglish ? "Skip video" : "Saltar video")}
          </button>
        </div>
      </div>
    </div>
  );
}
